import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { fetchAuditLog, type AuditLogEntry } from '../../lib/orders';
import { ACTION_LABELS, CHANNEL_BADGES, actorLabel } from '../../lib/audit-labels';

interface ActivityFeedProps {
  orderId: string;
  /** Incrementing value die een refetch triggert (bv. bumpen na save). */
  refreshKey?: number;
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diffSec = Math.round((Date.now() - then) / 1000);
  if (diffSec < 60) return 'zojuist';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m geleden`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}u geleden`;
  const d = new Date(iso);
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
}

export function ActivityFeed({ orderId, refreshKey = 0 }: ActivityFeedProps) {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchAuditLog(orderId)
      .then((rows) => {
        if (!cancelled) {
          setEntries(rows);
          setLoading(false);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, refreshKey]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
          Activiteit
        </h2>
        <p className="text-xs text-slate-400">Laden…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
          Activiteit
        </h2>
        <p className="text-xs text-red-600">{error}</p>
      </div>
    );
  }

  if (entries.length === 0) return null;

  const visible = expanded ? entries : entries.slice(0, 3);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
      <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
        Activiteit
      </h2>
      <ul className="space-y-2">
        {visible.map((entry) => {
          const badge = CHANNEL_BADGES[entry.channel];
          return (
            <li
              key={entry.id}
              className={clsx(
                'flex items-start gap-2 text-xs',
                entry.status === 'error' && 'bg-amber-50 rounded px-2 py-1',
                entry.status === 'geweigerd' && 'opacity-70'
              )}
            >
              <span className="text-slate-400">•</span>
              <div className="flex-1 min-w-0">
                <span className="text-slate-900 font-medium">{actorLabel(entry)}</span>
                <span className="text-slate-500">
                  {' '}— {entry.samenvatting ?? ACTION_LABELS[entry.action] ?? entry.action}
                </span>
                {badge && (
                  <span className={clsx('ml-1.5 text-[10px] px-1 rounded align-middle', badge.className)}>
                    {badge.label}
                  </span>
                )}
                <span className="text-slate-400"> · {relativeTime(entry.createdAt)}</span>
                {entry.status === 'error' && entry.errorMessage && (
                  <p className="text-amber-700 mt-0.5">⚠ {entry.errorMessage}</p>
                )}
                {entry.status === 'geweigerd' && (
                  <span className="ml-2 text-[10px] px-1 rounded bg-slate-100 text-slate-500">
                    geweigerd{entry.errorCode ? `: ${entry.errorCode}` : ''}
                  </span>
                )}
                {entry.status === 'pending' && (
                  <span className="ml-2 text-[10px] px-1 rounded bg-slate-100 text-slate-600">pending</span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {entries.length > 3 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 text-xs text-indigo-600 hover:underline"
        >
          {expanded ? 'Minder tonen' : `Meer tonen (${entries.length - 3})`}
        </button>
      )}
    </div>
  );
}
